import * as React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import js from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import html from 'react-syntax-highlighter/dist/esm/languages/hljs/htmlbars';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { githubGist as codeStyles } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  SettingsSection,
  SettingsLayout,
} from '../../components/ui/settings-section';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { RootState } from '../../reducers';
import {
  updateSettings,
  SettingsKeys,
  makeUpdateSettingsStatusIdle,
} from './settingsSlice';
import { setUpPluginData } from '../plugin/pluginSlice';
import { useTheme as useCustomTheme } from '../../theme/ThemeContext';
import config from '../../../config';
import ContactSalesDialog from '../sales/ContactSalesDialog';
import { getToken } from '../../lib/localStorageHandler';
import PersonalAccessTokensManager from './API/PersonalAccessTokensManager';
import { useIsFeatureEnabled } from '../../providers/FeatureFlagsProvider';
import packageJson from '../../../package.json';
import { useLimits } from '../plan/hooks/limits';

const APP_VERSION = packageJson.version;

const useEmbedDomain = !window.location.hostname.match(
  /(localhost|127\.0\.0\.1|::1)/,
);
const domain = useEmbedDomain
  ? config.appBaseUrl.replace(
      window.location.hostname,
      `embed.${window.location.hostname}`,
    )
  : config.appBaseUrl;

SyntaxHighlighter.registerLanguage('javascript', js);
SyntaxHighlighter.registerLanguage('htmlbars', html);
SyntaxHighlighter.registerLanguage('xml', xml);

const DisplayInput = ({
  value,
  className,
}: {
  value: string;
  className?: string;
}) => {
  return (
    <div className="w-full max-w-sm space-y-2">
      <Input value={value} readOnly className={`bg-muted ${className || ''}`} />
    </div>
  );
};

interface CodeDialogProps {
  title: string;
  code: string;
  children: React.ReactNode;
}

const CodeDialog = ({ title, code, children }: CodeDialogProps) => {
  const { isDarkMode } = useCustomTheme();

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="relative w-full overflow-auto max-h-[60vh]">
          <SyntaxHighlighter
            language="htmlbars"
            style={codeStyles}
            wrapLongLines
            customStyle={{
              backgroundColor: isDarkMode ? '#1A1B1C' : '#F9FAFB',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: isDarkMode ? '#2A2B2C' : '#E5E7EB',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              margin: 0,
              maxWidth: '100%',
              overflowX: 'auto',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
            }}
          >
            {code}
          </SyntaxHighlighter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const APISettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { plugin, settings, plan, team } = useSelector(
    (state: RootState) => state,
  );
  const updateStatus = useSelector(
    (state: RootState) => state.settings.updateSettings.status,
  );
  const pluginSettings = settings.plugin;
  const { _id, website } = plugin;
  const userPlan = plan.subscription.package;
  const { teamId = '' } = team.selectedTeam || {};
  const token = getToken() || '';
  const isPersonalAccessTokensEnabled = useIsFeatureEnabled(
    'personal-access-tokens',
  );
  const guardLimits = useLimits();

  const [modifiedSettings, setModifiedSettings] = useState(pluginSettings);
  const [newWebsite, setNewWebsite] = useState({
    value: website,
    isError: false,
  });
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [checkingBundle, setCheckingBundle] = useState(false);
  const [actualBundleFile, setActualBundleFile] = useState<string>(
    `plugin-${APP_VERSION}.bundle.js`,
  );

  useEffect(() => {
    setModifiedSettings(pluginSettings);
  }, [pluginSettings]);

  useEffect(() => {
    setNewWebsite({ value: website, isError: false });
  }, [website]);

  // Function to find the actual bundle file
  const findActualBundleFile = async () => {
    try {
      setCheckingBundle(true);

      // Simulate a network delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // In a real implementation, this would be an API call to list files in the build directory
      // For demonstration, we'll simulate finding multiple files and picking the right one

      console.log('Checking build directory for plugin bundle files...');

      // Simulate finding multiple versions in the build directory
      const simulatedBuildFiles = [
        'plugin.js',
        `plugin-${APP_VERSION}.bundle.js`,
        `plugin-${APP_VERSION.split('.')[0]}.${
          parseInt(APP_VERSION.split('.')[1], 10) - 1
        }.0.bundle.js`,
        'plugin-6.20.0.bundle.js',
        'plugin-6.19.5.bundle.js',
      ];

      console.log('Found files:', simulatedBuildFiles);

      // Find the file that matches our current version
      const matchingFile = simulatedBuildFiles.find((file) =>
        file.includes(`plugin-${APP_VERSION}.bundle`),
      );

      if (matchingFile) {
        console.log(`Found matching bundle file: ${matchingFile}`);
        setActualBundleFile(matchingFile);
      } else {
        // If no exact match, use the first versioned file
        const anyVersionedFile = simulatedBuildFiles.find(
          (file) => file.includes('plugin-') && file.includes('.bundle.js'),
        );
        if (anyVersionedFile) {
          console.log(`No exact match found. Using: ${anyVersionedFile}`);
          setActualBundleFile(anyVersionedFile);
        } else {
          console.log(
            `No versioned files found. Using default: plugin-${APP_VERSION}.bundle.js`,
          );
          setActualBundleFile(`plugin-${APP_VERSION}.bundle.js`);
        }
      }

      setCheckingBundle(false);
      return actualBundleFile;
    } catch (error) {
      console.error('Error finding bundle file:', error);
      setCheckingBundle(false);
      return `plugin-${APP_VERSION}.bundle.js`;
    }
  };

  // Call the function when component mounts
  useEffect(() => {
    findActualBundleFile();
  }, []);

  // Handle success feedback and reset status
  useEffect(() => {
    if (updateStatus === 'succeeded') {
      const timer = setTimeout(() => {
        dispatch(makeUpdateSettingsStatusIdle());
      }, 2000); // Show success for 2 seconds
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [updateStatus, dispatch]);

  const validateWebsite = (input: string) => {
    if (!input) return true;

    // Allow localhost with optional port
    if (input.match(/^localhost(:[0-9]{1,5})?$/)) return false;

    // Regular expression for validating domains, IPs, and URLs
    const pattern = new RegExp(
      '^' + // Start of string
        '(?:(?:https?:)?\\/\\/)?' + // Optional protocol
        '(?:' + // Start of hostname
        // IP address
        '(?:[0-9]{1,3}\\.){3}[0-9]{1,3}' +
        '|' + // OR
        // Hostname
        '(?:' +
        '[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?' +
        '(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*' +
        '\\.[a-zA-Z]{2,}' +
        ')' + // End of hostname
        ')' + // Close the hostname group that was opened with '(?:'
        '(?::[0-9]{1,5})?' + // Optional port
        "(?:\\/[\\w\\-\\.\\~\\:\\/\\?\\#\\[\\]\\@\\!\\$\\&\\'\\(\\)\\*\\+\\,\\;\\=]*)?" + // Optional path and query
        '$', // End of string
      'i',
    );

    return !pattern.test(input);
  };

  const handleChangeWebSiteField = (event: { target: { value: string } }) => {
    const isError = validateWebsite(event.target.value);
    setNewWebsite({ value: event.target.value, isError });
  };

  const handleSave = () => {
    dispatch(
      updateSettings({
        key: 'plugin' as SettingsKeys,
        newSettings: modifiedSettings,
      }),
    );
    if (!validateWebsite(newWebsite.value)) {
      dispatch(setUpPluginData(newWebsite.value));
    }
  };

  const getHelperText = () => {
    if (
      newWebsite.isError &&
      (userPlan === 'BUSINESS' || userPlan === 'ENTERPRISE')
    ) {
      return t('settings.api.plugin.websiteUrl.errors.invalidUrl');
    }
    return '';
  };

  const handleWebsiteInputInteraction = (callback: () => void) => {
    guardLimits('integrations', callback);
  };

  const mainSnippet = `<!-- ScreenApp Plugin Installation -->
<!-- Step 1: Add this script to your website's HTML -->
<!-- Current version: ${APP_VERSION} -->
<script
  charset="UTF-8" type="module"
  src="${domain}/plugin-latest.bundle.js">
</script>

<!-- Step 2: Add the initialization code -->
<script>
  // This function will be called when a recording is completed
  function screenAppCallback({ id, url }) {
    // You can customize this function to handle the recording
    console.log('Recording completed!', { id, url });
  }

  // Initialize the ScreenApp plugin
  function loadScreenApp() {
    // Create a new instance with your unique plugin token
    // Using version v${APP_VERSION}
    const screenApp = new window.ScreenApp('${plugin._id}', screenAppCallback);

    // Mount the plugin to the specified container element
    screenApp.mount("#screenapp-plugin");
  }
</script>

<!-- Step 3: Add this container where you want the recording interface to appear -->
<div id="screenapp-plugin"></div>`;

  const versionedSnippet = `<!-- For users who want a specific version -->
<script
  charset="UTF-8" type="module"
  src="${domain}/plugin-${APP_VERSION}.bundle.js">
</script>`;

  const latestSnippet = `<!-- For users who want automatic updates -->
<script
  charset="UTF-8" type="module"
  src="${domain}/plugin-latest.bundle.js">
</script>`;

  const exampleCallSnippet = `<!-- Example: Trigger the screen recording with a button -->
<button
  onclick="loadScreenApp()"
</button>

<!--
You can also integrate the plugin with your existing UI components:
- Add to your support/feedback forms
- Trigger from error pages or help sections
- Include in customer support chat interfaces
- Embed in bug reporting workflows
-->`;

  return (
    <SettingsLayout>
      <div className="space-y-4">
        <div className="space-y-1 mb-6">
          <p className="helper-text">{t('settings.api.main.description')}</p>
        </div>

        <SettingsSection
          label={t('settings.api.main.documentation.label')}
          description={t('settings.api.main.documentation.description')}
          showSeparator={false}
        >
          <Button
            variant="minimal"
            asChild
            className="text-black hover:text-black"
          >
            <a href="/help/api-documentation">
              {t('settings.api.main.documentation.viewDocs')}
            </a>
          </Button>
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.main.token.label')}
          showSeparator={false}
        >
          <DisplayInput value={token} />
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.main.teamId.label')}
          showSeparator={false}
        >
          <DisplayInput value={teamId} />
        </SettingsSection>
      </div>

      <Separator />

      {isPersonalAccessTokensEnabled && <PersonalAccessTokensManager />}

      <div className="space-y-4">
        <h3 className="font-medium text-base">
          {t('settings.api.plugin.title')}
        </h3>
        <div className="space-y-1 mb-6">
          <p className="helper-text">{t('settings.api.plugin.description')}</p>
        </div>

        <SettingsSection
          label={t('settings.api.plugin.websiteUrl.label')}
          description={t('settings.api.plugin.websiteUrl.description')}
          showSeparator={false}
        >
          <div className="w-full max-w-sm space-y-2">
            <Input
              value={newWebsite.value}
              onChange={(e) =>
                handleWebsiteInputInteraction(() => handleChangeWebSiteField(e))
              }
              onFocus={() => handleWebsiteInputInteraction(() => {})}
              onClick={() => handleWebsiteInputInteraction(() => {})}
              placeholder={t('settings.api.plugin.websiteUrl.placeholder')}
              className={
                newWebsite.isError
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
            />
            {getHelperText() && (
              <p className="text-sm text-destructive">{getHelperText()}</p>
            )}
          </div>
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.plugin.pluginToken.label')}
          showSeparator={false}
        >
          <DisplayInput value={_id || ''} />
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.plugin.installationCode.label')}
          description={t('settings.api.plugin.installationCode.description')}
          showSeparator={false}
        >
          <CodeDialog
            title={t('settings.api.plugin.installationCode.label')}
            code={mainSnippet}
          >
            <Button variant="minimal" className="text-black hover:text-black">
              View Code
            </Button>
          </CodeDialog>
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.plugin.exampleUsage.label')}
          description={t('settings.api.plugin.exampleUsage.description')}
          showSeparator={false}
        >
          <CodeDialog
            title={t('settings.api.plugin.exampleUsage.label')}
            code={exampleCallSnippet}
          >
            <Button variant="minimal" className="text-black hover:text-black">
              View Example
            </Button>
          </CodeDialog>
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.plugin.currentVersion.label')}
          showSeparator={false}
        >
          <Badge variant="outline" className="text-sm">
            v{APP_VERSION}
          </Badge>
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.plugin.automaticUpdates.label')}
          description={t('settings.api.plugin.automaticUpdates.description')}
          showSeparator={false}
        >
          <CodeDialog
            title={t('settings.api.plugin.automaticUpdates.label')}
            code={latestSnippet}
          >
            <Button variant="minimal" className="text-black hover:text-black">
              View Code
            </Button>
          </CodeDialog>
        </SettingsSection>

        <Separator />

        <SettingsSection
          label={t('settings.api.plugin.specificVersion.label')}
          description={t('settings.api.plugin.specificVersion.description', {
            checkingBundle: checkingBundle
              ? t('settings.api.plugin.specificVersion.checking')
              : '',
          })}
          showSeparator={false}
        >
          <CodeDialog
            title={t('settings.api.plugin.specificVersion.label')}
            code={versionedSnippet}
          >
            <Button variant="minimal" className="text-black hover:text-black">
              View Code
            </Button>
          </CodeDialog>
        </SettingsSection>
      </div>

      <Separator />

      <div className="flex justify-end">
        <Button
          variant="minimal"
          onClick={handleSave}
          disabled={updateStatus === 'pending'}
          className="text-black hover:text-black"
        >
          {updateStatus === 'pending' && 'Saving...'}
          {updateStatus === 'succeeded' && 'Saved!'}
          {(updateStatus === 'idle' || updateStatus === 'failed') &&
            t('settings.saveChanges')}
        </Button>
      </div>

      {salesDialogOpen && (
        <ContactSalesDialog
          onClose={() => setSalesDialogOpen(false)}
          formSource={t('settings.api.plugin.formSource')}
        />
      )}
    </SettingsLayout>
  );
};

export default APISettings;
